import { useEffect, useState } from "react";
import { Users, Repeat, DollarSign, Loader2, MapPin, Crown } from "lucide-react";
import api, { formatMoney } from "../lib/apiClient";

function Kpi({ icon: Icon, tone, label, value, sub, testid }) {
  return (
    <div className="card-soft rounded-2xl p-6" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="eyebrow">{label}</span>
        <span className={`w-9 h-9 rounded-xl flex items-center justify-center ${tone}`}><Icon className="w-4 h-4" /></span>
      </div>
      <p className="font-display font-extrabold text-2xl sm:text-3xl mt-3">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
    </div>
  );
}

export const CustomersPanel = ({ refreshing }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [days, setDays] = useState(90);
  const [month, setMonth] = useState(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    const params = month ? { month } : { days };
    const attempt = async (n) => {
      try {
        const r = await api.get("/admin/customers", { params });
        if (!cancelled) { setData(r.data); setLoading(false); }
      } catch (e) {
        if (cancelled) return;
        if (n < 3) { setTimeout(() => attempt(n + 1), 2000); return; }
        setData((prev) => (prev && !prev.error ? prev : { error: true }));
        setLoading(false);
      }
    };
    attempt(1);
    return () => { cancelled = true; };
  }, [refreshing, days, month]);

  const MONTHS = [];
  {
    const now = new Date();
    for (let i = 0; i < 12; i++) {
      const d = new Date(now.getFullYear(), now.getMonth() - i, 1);
      MONTHS.push({ value: `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`, label: d.toLocaleString("default", { month: "short", year: "numeric" }) });
    }
  }

  const Controls = () => (
    <div className="flex items-center gap-2 flex-wrap justify-end" data-testid="customers-controls">
      <div className="flex gap-1 bg-white border border-border rounded-xl p-1">
        {[30, 90].map((d) => (
          <button key={d} onClick={() => { setMonth(null); setDays(d); }} data-testid={`customers-range-${d}`}
            className={`px-3 py-1.5 rounded-lg text-xs font-semibold transition-colors ${!month && days === d ? "btn-gold" : "text-muted-foreground hover:text-foreground"}`}>
            {d}d
          </button>
        ))}
      </div>
      <select value={month || ""} onChange={(e) => setMonth(e.target.value || null)} data-testid="customers-month-select"
        className={`h-9 px-3 rounded-xl text-xs font-semibold border cursor-pointer outline-none ${month ? "btn-gold border-transparent" : "bg-white border-border text-foreground"}`}>
        <option value="">By month…</option>
        {MONTHS.map((m) => <option key={m.value} value={m.value} className="text-foreground bg-white">{m.label}</option>)}
      </select>
    </div>
  );

  const Header = () => (
    <div className="flex items-start justify-between gap-3 flex-wrap">
      <div>
        <h2 className="font-display font-bold text-lg">Customers</h2>
        <p className="text-xs text-muted-foreground" data-testid="customers-window-label">{data?.window_label}</p>
      </div>
      <Controls />
    </div>
  );

  if (loading) return (
    <div className="space-y-6" data-testid="customers-panel">
      <Header />
      <div className="card-soft rounded-2xl p-12 flex items-center justify-center gap-3 text-muted-foreground" data-testid="customers-loading"><Loader2 className="w-5 h-5 animate-spin" /> Analyzing WooCommerce customers…</div>
    </div>
  );
  if (!data || data.configured === false) return <div className="card-soft rounded-2xl p-8 text-muted-foreground">WooCommerce is not connected.</div>;
  if (data.error) return (
    <div className="space-y-6" data-testid="customers-panel"><Header />
      <div className="card-soft rounded-2xl p-8 text-muted-foreground" data-testid="customers-error">Loading live data from WooCommerce… one moment.</div>
    </div>
  );

  const maxLoc = Math.max(1, ...(data.top_locations || []).map((l) => l.revenue));

  return (
    <div className="space-y-6" data-testid="customers-panel">
      <Header />
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
        <Kpi testid="cust-kpi-unique" icon={Users} tone="bg-blue-500/15 text-blue-600" label="Unique customers" value={data.totals.unique_customers} sub={`${data.totals.repeat_customers} ordered more than once`} />
        <Kpi testid="cust-kpi-repeat" icon={Repeat} tone="bg-emerald-500/15 text-emerald-600" label="Repeat rate" value={`${data.totals.repeat_rate}%`} sub="Customers with 2+ orders" />
        <Kpi testid="cust-kpi-revenue" icon={DollarSign} tone="bg-amber-500/15 text-amber-600" label="Paid revenue" value={formatMoney(data.totals.revenue)} />
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        {/* Top customers */}
        <div className="lg:col-span-3 card-soft rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-border flex items-center gap-2">
            <Crown className="w-4 h-4" style={{ color: "var(--gold)" }} />
            <h3 className="font-display font-bold text-lg">Largest customers</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="top-customers-table">
              <thead><tr className="text-left text-muted-foreground text-xs uppercase tracking-wider border-b border-border bg-[#f6f1e6]">
                <th className="px-5 py-3 font-bold">Customer</th>
                <th className="px-5 py-3 font-bold hidden md:table-cell">Location</th>
                <th className="px-5 py-3 font-bold text-center">Orders</th>
                <th className="px-5 py-3 font-bold text-right">Total spent</th>
              </tr></thead>
              <tbody>
                {(data.top_customers || []).length === 0 ? (
                  <tr><td colSpan={4} className="px-5 py-12 text-center text-muted-foreground" data-testid="no-customers">No paid customers in this window.</td></tr>
                ) : (data.top_customers || []).map((c, i) => (
                  <tr key={c.email || c.name || i} data-testid={`customer-row-${i}`} className="border-b border-border last:border-0 hover:bg-[#f9f6ef] transition-colors">
                    <td className="px-5 py-4">
                      <div className="flex items-center gap-2">
                        {i < 3 && <span className="w-5 h-5 rounded-full bg-gradient-to-br from-[#e6c665] to-[#a47d15] text-white text-[10px] font-bold flex items-center justify-center shrink-0">{i + 1}</span>}
                        <div className="min-w-0">
                          <p className="font-semibold truncate">{c.name}</p>
                          <p className="text-xs text-muted-foreground truncate">{c.email || "—"}</p>
                        </div>
                      </div>
                    </td>
                    <td className="px-5 py-4 text-muted-foreground hidden md:table-cell">{c.location || "—"}</td>
                    <td className="px-5 py-4 text-center">{c.orders}</td>
                    <td className="px-5 py-4 text-right font-display font-bold gold-text">{formatMoney(c.total)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        {/* Top locations */}
        <div className="lg:col-span-2 card-soft rounded-2xl overflow-hidden">
          <div className="p-6 border-b border-border flex items-center gap-2">
            <MapPin className="w-4 h-4" style={{ color: "var(--gold)" }} />
            <h3 className="font-display font-bold text-lg">Where sales go</h3>
          </div>
          <div className="p-5 space-y-3" data-testid="top-locations">
            {(data.top_locations || []).length === 0 ? (
              <p className="text-muted-foreground text-sm py-8 text-center">No location data.</p>
            ) : (data.top_locations || []).map((l, i) => (
              <div key={l.label} data-testid={`location-row-${i}`}>
                <div className="flex items-center justify-between text-sm mb-1">
                  <span className="font-semibold truncate">{l.label}</span>
                  <span className="font-display font-bold tabular-nums">{formatMoney(l.revenue)}</span>
                </div>
                <div className="h-2 rounded-full bg-secondary overflow-hidden">
                  <div className="h-full rounded-full bg-gradient-to-r from-[#e6c665] to-[#c59b27]" style={{ width: `${Math.max(4, (l.revenue / maxLoc) * 100)}%` }} />
                </div>
                <p className="text-[11px] text-muted-foreground mt-0.5">{l.orders} order{l.orders === 1 ? "" : "s"}</p>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
};
